import { useState } from "react";
import { useLocation } from "react-router-dom";
import { PageLayout } from "../../components/common/layout/pageLayout";
import { UserInfo } from "../../components/profile/user/info";
import { GymInfoWithEmailConfig } from "../../components/profile/gym/info";
import { AboutTab } from "../../components/profile/about/aboutTab";
import { useAuth } from "../../contexts/auth/Context";
import { decodeUserInfo } from "../../utils/user";

type ProfileTab = "user" | "gym" | "about";

const UserIcon = () => (
  <svg
    className="w-4 h-4 shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
);

const BuildingIcon = () => (
  <svg
    className="w-4 h-4 shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
  </svg>
);

const InfoIcon = () => (
  <svg
    className="w-4 h-4 shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

export const UserProfilePage = () => {
  const location = useLocation();
  const [currentTab, setCurrentTab] = useState<ProfileTab>("user");
  const [prevNavTab, setPrevNavTab] = useState<ProfileTab | null>(null);

  const navTab = (location.state as { tab?: ProfileTab } | null)?.tab;
  const validNavTab =
    navTab === "gym" || navTab === "user" || navTab === "about" ? navTab : null;
  if (validNavTab !== null && validNavTab !== prevNavTab) {
    setPrevNavTab(validNavTab);
    setCurrentTab(validNavTab);
  }

  const { token } = useAuth();
  const { role } = decodeUserInfo(token);
  const isSuperAdmin = role?.trim().toUpperCase() === "SUPERADMIN";

  const tabs = [
    { key: "user" as ProfileTab, label: "Usuario", icon: <UserIcon /> },
    ...(!isSuperAdmin
      ? [{ key: "gym" as ProfileTab, label: "Negocio", icon: <BuildingIcon /> }]
      : []),
    { key: "about" as ProfileTab, label: "DashCore", icon: <InfoIcon /> },
  ];

  return (
    <PageLayout>
      <div className="flex flex-col gap-compact max-w-4xl mx-auto">
        {/* Tab Navigation */}
        <nav className="card-compact p-1.5">
          <div className="flex items-center gap-1">
            {tabs.map(({ key, label, icon }) => (
              <button
                key={key}
                onClick={() => setCurrentTab(key)}
                className={`flex items-center justify-center gap-2 btn-compact-sm flex-1 cursor-pointer transition-all duration-200 ${
                  currentTab === key
                    ? "btn-global-primary text-white shadow-global-button"
                    : "bg-transparent text-global-tertiary hover:text-global-primary hover:bg-global-overlay-secondary"
                }`}
              >
                {icon}
                <span>{label}</span>
              </button>
            ))}
          </div>
        </nav>

        {/* Tab Content */}
        {currentTab === "about" ? (
          <AboutTab />
        ) : currentTab === "gym" && !isSuperAdmin ? (
          <GymInfoWithEmailConfig />
        ) : (
          <UserInfo />
        )}
      </div>
    </PageLayout>
  );
};
